/**
 * Settings store for user preferences.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';

// =============================================================================
// TYPES
// =============================================================================

export type Theme = 'light' | 'dark' | 'system';
export type ViewMode = 'map' | 'table';
export type HexResolution = 6 | 7 | 8;

interface SettingsStoreState {
  // Accessibility mode (shows table view)
  accessibilityMode: boolean;

  // Current view mode
  viewMode: ViewMode;

  // Theme preference
  theme: Theme;

  // Show Poller Bounding Boxes
  pollerBboxEnabled: boolean;

  // Show V4 position and deviation line for selected vehicle
  showSelectedV4Position: boolean;

  // Show freshness dots on vehicle markers (admin-only)
  showFreshnessDots: boolean;

  // Show H3 hex density overlay (admin-only)
  hexDensityEnabled: boolean;

  // H3 hex resolution (admin-only, 6=City ~3.7km, 7=District ~1.4km, 8=Neighborhood ~460m)
  hexResolution: HexResolution;

  // Enable V5 segment-based animation (admin-only)
  segmentAnimationEnabled: boolean;

  // Show V5 segment-computed position marker for selected vehicle (admin-only)
  showV5Position: boolean;

  // Show V1-DA GPS debug overlay on selected vehicle (admin-only)
  showV1DAPosition: boolean;

  // Position Sources section (logged-in users) — independent crosshairs for each source
  showPositionV1DA: boolean;
  showPositionV4: boolean;
  showPositionV5: boolean;

  // Enable smooth vehicle animation (interpolation between position updates)
  smoothAnimationEnabled: boolean;

  // Debug mode
  debugMode: boolean;

  // Show performance stats overlay (requires debugMode)
  showPerformanceStats: boolean;

  // Actions
  setAccessibilityMode: (enabled: boolean) => void;
  setViewMode: (mode: ViewMode) => void;
  setTheme: (theme: Theme) => void;
  setPollerBboxEnabled: (enabled: boolean) => void;
  setShowSelectedV4Position: (enabled: boolean) => void;
  setShowFreshnessDots: (enabled: boolean) => void;
  setHexDensityEnabled: (enabled: boolean) => void;
  setHexResolution: (resolution: HexResolution) => void;
  setSegmentAnimationEnabled: (enabled: boolean) => void;
  setShowV5Position: (enabled: boolean) => void;
  setShowV1DAPosition: (enabled: boolean) => void;
  setShowPositionV1DA: (enabled: boolean) => void;
  setShowPositionV4: (enabled: boolean) => void;
  setShowPositionV5: (enabled: boolean) => void;
  setSmoothAnimationEnabled: (enabled: boolean) => void;
  setDebugMode: (enabled: boolean) => void;
  setShowPerformanceStats: (enabled: boolean) => void;
  toggleAccessibilityMode: () => void;
  toggleViewMode: () => void;
}

// =============================================================================
// STORE
// =============================================================================

export const useSettingsStore = create<SettingsStoreState>()(
  persist(
    immer((set) => ({
      accessibilityMode: false,
      viewMode: 'map',
      theme: 'system',
      pollerBboxEnabled: false,
      showSelectedV4Position: false,  // Admin-only: show deviation line between V4 and V1-DA positions
      showFreshnessDots: false,  // Admin-only: show per-source freshness dots on markers
      hexDensityEnabled: false,  // Admin-only: show H3 hex density overlay
      hexResolution: 7 as HexResolution,  // Admin-only: H3 display resolution (District ~1.4km)
      segmentAnimationEnabled: false,  // Deprecated: no longer affects main marker
      showV5Position: false,  // Admin-only: show V5 computed position marker
      showV1DAPosition: false,  // Admin-only: show V1-DA GPS debug overlay
      showPositionV1DA: false,  // Position Sources: V1-DA crosshair (logged-in)
      showPositionV4: false,    // Position Sources: V4 crosshair (logged-in)
      showPositionV5: false,    // Position Sources: V5 crosshair (logged-in)
      smoothAnimationEnabled: true,  // Enable smooth animation by default
      debugMode: false,
      showPerformanceStats: false,

      setAccessibilityMode: (enabled) => {
        set((state) => {
          state.accessibilityMode = enabled;
          if (enabled) {
            state.viewMode = 'table';
          }
        });
      },

      setViewMode: (mode) => {
        set((state) => {
          state.viewMode = mode;
        });
      },

      setTheme: (theme) => {
        set((state) => {
          state.theme = theme;
        });
      },

      setPollerBboxEnabled: (enabled) => {
        set((state) => {
          state.pollerBboxEnabled = enabled;
        });
      },

      setShowSelectedV4Position: (enabled) => {
        set((state) => {
          state.showSelectedV4Position = enabled;
        });
      },

      setShowFreshnessDots: (enabled) => {
        set((state) => {
          state.showFreshnessDots = enabled;
        });
      },

      setHexDensityEnabled: (enabled) => {
        set((state) => {
          state.hexDensityEnabled = enabled;
        });
      },

      setHexResolution: (resolution) => {
        set((state) => {
          state.hexResolution = resolution;
        });
      },

      setSegmentAnimationEnabled: (enabled) => {
        set((state) => {
          state.segmentAnimationEnabled = enabled;
        });
      },

      setShowV5Position: (enabled) => {
        set((state) => {
          state.showV5Position = enabled;
        });
      },

      setShowV1DAPosition: (enabled) => {
        set((state) => {
          state.showV1DAPosition = enabled;
        });
      },

      setShowPositionV1DA: (enabled) => {
        set((state) => {
          state.showPositionV1DA = enabled;
        });
      },

      setShowPositionV4: (enabled) => {
        set((state) => {
          state.showPositionV4 = enabled;
        });
      },

      setShowPositionV5: (enabled) => {
        set((state) => {
          state.showPositionV5 = enabled;
        });
      },

      setSmoothAnimationEnabled: (enabled) => {
        set((state) => {
          state.smoothAnimationEnabled = enabled;
        });
      },

      setDebugMode: (enabled) => {
        set((state) => {
          state.debugMode = enabled;
          // Auto-disable sub-features when debug mode is turned off
          if (!enabled) {
            state.showPerformanceStats = false;
          }
        });
      },

      setShowPerformanceStats: (enabled) => {
        set((state) => {
          state.showPerformanceStats = enabled;
        });
      },

      toggleAccessibilityMode: () => {
        set((state) => {
          state.accessibilityMode = !state.accessibilityMode;
          if (state.accessibilityMode) {
            state.viewMode = 'table';
          }
        });
      },

      toggleViewMode: () => {
        set((state) => {
          state.viewMode = state.viewMode === 'map' ? 'table' : 'map';
        });
      },
    })),
    {
      name: 'vasttrafik-settings-store',
      // Migrate: handle schema changes across versions
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Record<string, unknown>;
        if (version < 2 && state) {
          // Remove deprecated properties
          delete state.v4PositionEnabled;
          delete state.deviationLinesEnabled;
          delete state.ghostEnabled;
          // Set new default
          if (!('showSelectedV4Position' in state)) {
            state.showSelectedV4Position = true;
          }
        }
        if (version < 3 && state) {
          // Add debug mode properties
          if (!('debugMode' in state)) {
            state.debugMode = false;
          }
          if (!('showPerformanceStats' in state)) {
            state.showPerformanceStats = false;
          }
        }
        if (version < 4 && state) {
          // Add smooth animation
          if (!('smoothAnimationEnabled' in state)) {
            state.smoothAnimationEnabled = true;
          }
        }
        if (version < 5 && state) {
          // Remove deprecated discovery grid toggle
          delete state.discoveryGridEnabled;
        }
        if (version < 6 && state) {
          // Deviation line is now admin-only and off by default
          state.showSelectedV4Position = false;
        }
        if (version < 7 && state) {
          if (!('showFreshnessDots' in state)) {
            state.showFreshnessDots = false;
          }
        }
        if (version < 8 && state) {
          if (!('hexDensityEnabled' in state)) {
            state.hexDensityEnabled = false;
          }
        }
        if (version < 9 && state) {
          if (!('segmentAnimationEnabled' in state)) {
            state.segmentAnimationEnabled = true;
          }
        }
        if (version < 10 && state) {
          if (!('hexResolution' in state)) {
            state.hexResolution = 7;
          }
        }
        if (version < 11 && state) {
          if (!('showV5Position' in state)) {
            state.showV5Position = false;
          }
        }
        if (version < 12 && state) {
          // segmentAnimationEnabled no longer controls main marker position —
          // reset to false for all users to prevent stale true values
          state.segmentAnimationEnabled = false;
        }
        if (version < 13 && state) {
          if (!('showV1DAPosition' in state)) {
            state.showV1DAPosition = false;
          }
        }
        if (version < 14 && state) {
          if (!('showPositionV1DA' in state)) {
            state.showPositionV1DA = false;
          }
          if (!('showPositionV4' in state)) {
            state.showPositionV4 = false;
          }
          if (!('showPositionV5' in state)) {
            state.showPositionV5 = false;
          }
        }
        return state as unknown as SettingsStoreState;
      },
      version: 14,
    }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

export const selectAccessibilityMode = (state: SettingsStoreState) => state.accessibilityMode;
export const selectViewMode = (state: SettingsStoreState) => state.viewMode;
export const selectTheme = (state: SettingsStoreState) => state.theme;
export const selectPollerBboxEnabled = (state: SettingsStoreState) => state.pollerBboxEnabled;
export const selectShowSelectedV4Position = (state: SettingsStoreState) => state.showSelectedV4Position;
export const selectSmoothAnimationEnabled = (state: SettingsStoreState) => state.smoothAnimationEnabled;
export const selectDebugMode = (state: SettingsStoreState) => state.debugMode;
export const selectShowPerformanceStats = (state: SettingsStoreState) => state.showPerformanceStats;
export const selectShowFreshnessDots = (state: SettingsStoreState) => state.showFreshnessDots;
export const selectHexDensityEnabled = (state: SettingsStoreState) => state.hexDensityEnabled;
export const selectHexResolution = (state: SettingsStoreState) => state.hexResolution;
export const selectSegmentAnimationEnabled = (state: SettingsStoreState) => state.segmentAnimationEnabled;
export const selectShowV5Position = (state: SettingsStoreState) => state.showV5Position;
export const selectShowV1DAPosition = (state: SettingsStoreState) => state.showV1DAPosition;
export const selectShowPositionV1DA = (state: SettingsStoreState) => state.showPositionV1DA;
export const selectShowPositionV4 = (state: SettingsStoreState) => state.showPositionV4;
export const selectShowPositionV5 = (state: SettingsStoreState) => state.showPositionV5;
